/* Copyright (c) 2020-present CloudIO Inc */

import { useLatest, useStateXGetter } from '@cloudio-saas/statex';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import cx from 'clsx';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { runtimePath } from 'designer/atoms';
import { usePending, usePlatform } from 'platform/PlatformHooks';
import React, {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useStoreBusy } from 'store/useAppStore';
import {
  backgroundColorStyleFn,
  readableColorStyleFn,
} from 'theme/useThemeColor';
import useJoinRef from 'utils/useJoinRef';
import { isNull } from '../../../utils/isEmpty';
import { useIsRTL } from '../../../utils/state';
import ColumnMenuWrapper from '../ColumnMenu';
import ColumnReorderArrow from '../ColumnReorderArrow';
import { FocusCellContext, GridBoundsContext } from '../Context';
import Body from './Body';
import Footer from './Footer';
import GridFilter from './GridFilter';
import GridLoadingIndicator from './GridLoadingIndicator';
import { ScrollValueProvider, useScrollValue } from './GridScroll';
import { getScrollbarSize, makePosition } from './GridUtils';
import Header from './Header';
import { GridCache, GridProps } from './types';
import { useGridBoundsMetaData, useGridMetadata } from './useGridMetadata';

const useStyles = makeStyles((theme: Theme) => ({
  gridWrapper: {
    backgroundColor: backgroundColorStyleFn(theme),
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'content-box',
    margin: 0,
    padding: 0,
    border: 0,
    userSelect: 'none',
    color: readableColorStyleFn(theme),
  },
}));

const useGridStyles = makeStyles((theme: Theme) => ({
  gridBorder: {
    border: `1px solid ${theme.palette.divider}`,
  },
}));

function usePrevious(value: string) {
  const ref = useRef<string>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const GridInternalFn = React.forwardRef(
  (props: GridProps, ref: React.Ref<HTMLDivElement>): React.ReactElement => {
    const {
      CellComponent,
      appUid,
      roleUid,
      backgroundColor,
      bodyProps,
      bodyRef,
      className,
      columnWidth,
      columns,
      custom,
      dataContext,
      fixedLeftColumns = 0,
      fixedRightColumns = 0,
      footerRows = 0,
      headerRows = 1,
      height,
      highlightCell,
      highlightRow,
      itemId,
      noDataMsg,
      showNoDataMsg,
      onClick,
      onRowDoubleClick,
      pageId,
      rowHeight,
      scrollToColumn = 0,
      scrollToRow = 0,
      showColumnLines,
      showRowLines,
      totalColumns,
      totalRows,
      width,
      disableReorder,
      expandLastColumn,
      autoFitColumns,
      listStore,
      queryOnNext,
      rowBuffer,
      columnBuffer,
      allowDownArrowRecordCreation,
      h1LeftFixedColumnGroups,
      h1RightFixedColumnGroups,
      h1BodyColumnGroups,
      h2LeftFixedColumnGroups,
      h2RightFixedColumnGroups,
      h2BodyColumnGroups,
      h3LeftFixedColumnGroups,
      h3RightFixedColumnGroups,
      h3BodyColumnGroups,
      h1GroupColumns,
      h2GroupColumns,
      h3GroupColumns,
      h1FixedLeftColumns = 0,
      h1FixedRightColumns = 0,
      h2FixedLeftColumns = 0,
      h2FixedRightColumns = 0,
      h3FixedLeftColumns = 0,
      h3FixedRightColumns = 0,
    } = props;
    const platform = usePlatform();

    const classes = useStyles({ backgroundColor });

    const isRTL = useIsRTL();
    const direction = isRTL ? 'rtl' : 'ltr';
    const headerHeight = headerRows ? headerRows * rowHeight : 0;
    const footerHeight = footerRows ? footerRows * rowHeight : 0;
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const joinWrapperRef = useJoinRef(wrapperRef, ref);
    const get = useStateXGetter();

    const gridId = usePrevious(itemId);

    const [bodyHeight, setBodyHeight] = useState(
      height - headerHeight - footerHeight,
    );
    const [bodyWidth, setBodyWidth] = useState(-1);
    // const [columnToFocus, setColumnToFocus] = useState(-1);
    // const [rowToFocus, setRowToFocus] = useState(-1);

    const scrollHeight = rootRef.current?.scrollHeight || 0;
    const scrollWidth = rootRef.current?.scrollWidth || 0;

    // console.log('before useGridMetadata');
    const {
      columnLefts,
      columnWidths,
      innerWidth = 0,
      onKeyDown,
      focusSignal,
      boundSignal,
      h1GroupColumnLefts,
      h1GroupColumnWidths,
      h2GroupColumnLefts,
      h2GroupColumnWidths,
      h3GroupColumnLefts,
      h3GroupColumnWidths,
    } = useGridMetadata({
      bodyHeight,
      columnWidthFn: columnWidth,
      columns,
      expandLastColumn,
      autoFitColumns,
      footerRows,
      headerRows,
      height,
      rowHeight,
      scrollToColumn,
      scrollToRow,
      totalColumns,
      totalRows,
      width,
      allowDownArrowRecordCreation,
      gridItemId: itemId,
      pageId,
      appUid,
      h1GroupColumns,
      h2GroupColumns,
      h3GroupColumns,
      h1LeftFixedColumnGroups,
      h1RightFixedColumnGroups,
      h1BodyColumnGroups,
      h2LeftFixedColumnGroups,
      h2RightFixedColumnGroups,
      h2BodyColumnGroups,
      h3LeftFixedColumnGroups,
      h3RightFixedColumnGroups,
      h3BodyColumnGroups,
    });

    useGridBoundsMetaData({
      bodyHeight,
      bodyWidth,
      columnBuffer,
      columnWidths,
      fixedLeftColumns,
      fixedRightColumns,
      footerRows,
      headerRows,
      rowBuffer,
      rowHeight,
      scrollHeight,
      scrollWidth,
      totalColumns,
      totalRows,
      gridBoundsSignal: boundSignal,
    });

    const mv = useScrollValue();
    useEffect(() => {
      return mv.pubsub.sub('OnChange', async (_event, { x, y }) => {
        startTransition(() => {
          if (!isNull(x)) {
            boundSignal.pos.left.set(-x);
          }
          if (!isNull(y)) {
            boundSignal.pos.top.set(-y);
          }
        });
      });
    }, [boundSignal, boundSignal.pos.top, mv.pubsub]);

    //reset the scrollbar position on tab switch
    useLayoutEffect(() => {
      if (gridId && gridId !== itemId) {
        const yValue: number =
          get(runtimePath({ appUid, pageId, itemId, key: 'top' })) ?? 0;
        mv.wheeling = true;
        mv.set({ y: -yValue });
        boundSignal.pos.top.set(yValue);
      }
    }, [appUid, boundSignal, get, gridId, itemId, mv, pageId]);

    const leftFixedWidth: number = fixedLeftColumns
      ? columnLefts[fixedLeftColumns - 1] + columnWidths[fixedLeftColumns - 1]
      : 0;
    const rightFixedWidth: number = fixedRightColumns
      ? innerWidth - columnLefts[totalColumns - fixedRightColumns]
      : 0;

    const actualBodyHeight = (totalRows - headerRows - footerRows) * rowHeight;
    const h = Math.max(actualBodyHeight, height - headerHeight - footerHeight);

    const _scrollbarSize = getScrollbarSize();
    const vertialScrollbarSize = actualBodyHeight < h ? 0 : _scrollbarSize;
    const horizontalScrollbarSize = innerWidth > width ? _scrollbarSize : 0;

    mv.xMax = innerWidth - width - vertialScrollbarSize;
    mv.yMax = totalRows * rowHeight - height + horizontalScrollbarSize;

    const cacheRef = useRef<GridCache>({
      bodyHeight,
      bodyWidth,
      columnLefts,
      columnWidths,
      fixedLeftColumns,
      fixedRightColumns,
      footerHeight,
      footerRows,
      headerHeight,
      headerRows,
      height,
      horizontalScrollbarSize,
      leftFixedWidth,
      onClick,
      rightFixedWidth,
      rowHeight,
      scrollHeight,
      scrollWidth,
      totalColumns,
      totalRows,
      vertialScrollbarSize,
      width,
      rowBuffer,
      columnBuffer,
    });
    if (cacheRef.current.scrollHeight !== scrollHeight) {
      cacheRef.current.scrollHeight = scrollHeight;
    }

    useLayoutEffect(() => {
      cacheRef.current = {
        ...cacheRef.current,
        bodyHeight,
        bodyWidth,
        columnLefts,
        columnWidths,
        fixedLeftColumns,
        fixedRightColumns,
        footerHeight,
        footerRows,
        headerHeight,
        headerRows,
        height,
        horizontalScrollbarSize,
        leftFixedWidth,
        onClick,
        rightFixedWidth,
        rowHeight,
        scrollHeight,
        scrollWidth,
        totalColumns,
        totalRows,
        vertialScrollbarSize,
        width,
      };
    }, [
      bodyHeight,
      bodyWidth,
      columnLefts,
      columnWidths,
      fixedLeftColumns,
      fixedRightColumns,
      footerHeight,
      footerRows,
      headerHeight,
      headerRows,
      height,
      horizontalScrollbarSize,
      leftFixedWidth,
      onClick,
      rightFixedWidth,
      rowHeight,
      scrollHeight,
      scrollWidth,
      totalColumns,
      totalRows,
      vertialScrollbarSize,
      width,
    ]);
    const [pending, setPending] = usePending();
    const store = useCurrentDataStore();
    const busy = useStoreBusy(store);
    const counterRef = useRef(0);
    const runningRef = useRef(false);
    if (busy) {
      counterRef.current++;
    }
    const count = counterRef.current;
    useEffect(() => {
      // custom will be true when Grid is used outside
      // DataGrid component e.g. Select, sidebar etc.
      // we must not do anything to the store in such case
      // as store won't be used.
      // if this grid is used by Select, then this store
      // is from it's parent container e.g. Form or DataGrid

      function run(endRow: number) {
        if (store && !custom && !runningRef.current) {
          const size = store.getSize();
          if (
            store.hasInitialQueryFired() &&
            endRow >= size - 10 &&
            store.hasMore &&
            !busy &&
            !store.hasPagination
          ) {
            runningRef.current = true;
            platform.startAsyncTransition(
              async () => {
                try {
                  await store.next();
                  setTimeout(() => {
                    // when wheeling is active cacheRef.current.scrollHeight is
                    // not getting updated as useLayoutEffect is not getting fired
                    // sometimes
                    if (rootRef.current) {
                      cacheRef.current.scrollHeight =
                        rootRef.current.scrollHeight || 0;
                    }
                  }, 100);
                } finally {
                  runningRef.current = false;
                }
              },
              setPending,
              { itemId: dataContext.itemId, pageId, appUid },
            );
          }
        }

        if (custom && listStore && queryOnNext && !runningRef.current) {
          const size = listStore.getSize();
          if (
            listStore.hasInitialQueryFired() &&
            size > 0 &&
            endRow >= size - 30 &&
            listStore.hasMore &&
            !busy
          ) {
            runningRef.current = true;
            platform.startAsyncTransition(
              async () => {
                try {
                  await listStore.next();
                  setTimeout(() => {
                    // when wheeling is active cacheRef.current.scrollHeight is
                    // not getting updated as useLayoutEffect is not getting fired
                    // sometimes
                    if (rootRef.current) {
                      cacheRef.current.scrollHeight =
                        rootRef.current.scrollHeight || 0;
                    }
                  }, 100);
                } finally {
                  runningRef.current = false;
                }
              },
              setPending,
              { itemId: dataContext.itemId, pageId, appUid },
            );
          }
        }
      }
      run(boundSignal.bound.row.end.peek());
      return boundSignal.bound.row.end.onChange((endRow) => {
        run(endRow);
      });
    }, [
      platform,
      setPending,
      store,
      busy,
      count,
      custom,
      listStore,
      queryOnNext,
      dataContext.itemId,
      pageId,
      appUid,
      boundSignal,
    ]);

    const resetScroll = useCallback(() => {
      boundSignal.pos.set({ left: 0, top: 0 });
    }, [boundSignal]);

    useLayoutEffect(() => {
      if (!vertialScrollbarSize) {
        boundSignal.pos.top.set(0);
      }
      if (!horizontalScrollbarSize) {
        boundSignal.pos.left.set(0);
      }
    }, [boundSignal, horizontalScrollbarSize, vertialScrollbarSize]);

    useLayoutEffect(() => {
      // initial render
      if (rootRef.current) {
        // avoid resetting the scroll position on window resize
        // setTop(0);
        // setLeft(0);
        const bh =
          height - headerHeight - footerHeight - horizontalScrollbarSize;
        const bw =
          width - leftFixedWidth - rightFixedWidth - vertialScrollbarSize;
        setBodyHeight(bh);
        setBodyWidth(bw);
      }
    }, [
      width,
      height,
      leftFixedWidth,
      rightFixedWidth,
      vertialScrollbarSize,
      headerHeight,
      footerHeight,
      horizontalScrollbarSize,
    ]);

    const focusRef = useLatest({
      columnLefts,
      columnWidths,
      fixedLeftColumns,
      fixedRightColumns,
      footerRows,
      headerRows,
      height,
      horizontalScrollbarSize,
      leftFixedWidth,
      rowHeight,
      totalColumns,
      totalRows,
      vertialScrollbarSize,
      width,
    });

    useLayoutEffect(() => {
      // handle focus change

      return focusSignal.onChange((focusCell) => {
        const { focusColumn, focusRow } = focusCell;

        const {
          columnLefts: _columnLefts,
          columnWidths: _columnWidths,
          fixedLeftColumns: _fixedLeftColumns,
          fixedRightColumns: _fixedRightColumns,
          footerRows: _footerRows,
          headerRows: _headerRows,
          horizontalScrollbarSize: _horizontalScrollbarSize,
          leftFixedWidth: _leftFixedWidth,
          rowHeight: _rowHeight,
          totalColumns: _totalColumns,
          totalRows: _totalRows,
          vertialScrollbarSize: _vertialScrollbarSize,
        } = focusRef.current;

        const ele = rootRef.current;
        // skip this for the first render where ele.clientHeight will be zero
        if (ele?.clientHeight) {
          const col = Math.max(0, Math.min(focusColumn, _totalColumns - 1));
          const row = Math.max(0, Math.min(focusRow, _totalRows - 1));
          const pos = makePosition(ele, isRTL);
          let _left = 0;
          let forceLeft = false;
          let forceTop = false;
          if (
            col < _fixedLeftColumns ||
            col > _totalColumns - 1 - _fixedRightColumns
          ) {
            // focus is on a fixed column... hence keep the current scroll left
            _left = pos.left;
          } else {
            _left = _columnLefts[col] - _leftFixedWidth;
            if (_left + _columnWidths[col] > pos.left + pos.bodyWidth) {
              forceLeft = true;
              _left = _left + _columnWidths[col] - pos.bodyWidth;
            }
          }
          let _top = 0;
          if (row < _headerRows || row > _totalRows - 1 - _footerRows) {
            // focus is on a fixed row... hence keep the current scroll top
            _top = pos.top;
          } else {
            _top = Math.max(0, row - _headerRows) * _rowHeight;
            if (_top + _rowHeight > pos.top + pos.bodyHeight) {
              forceTop = true;
              _top = _top + _rowHeight - pos.bodyHeight;
            }
          }
          let x = -pos.left;
          // do not change the left if it's already in visible region
          if (
            forceLeft ||
            _left < pos.left ||
            _left > pos.left + pos.bodyWidth
          ) {
            x = _horizontalScrollbarSize ? -_left : 0;
          }
          let y = -pos.top;
          // do not change the top if it's already in visible region
          if (forceTop || _top < pos.top || _top > pos.top + pos.bodyHeight) {
            y = _vertialScrollbarSize ? -_top : 0;
          }

          if (x !== -pos.left || y !== -pos.top) {
            mv.set({
              x,
              y,
            });
            // to update the scrollbars
            mv.pubsub.pub('UpdateScroll', { x, y });
          }
        }
      });
      // only change scroll position when either focusColumn or focusRow is changed
    }, [focusRef, focusSignal, isRTL, mv]);

    // useWhyDidYouUpdate('Grid', {
    //   ...props,
    //   bodyHeight,
    //   bodyWidth,
    //   columnLefts,
    //   columnWidths,
    //   fixedLeftColumns,
    //   fixedRightColumns,
    //   footerHeight,
    //   footerRows,
    //   headerHeight,
    //   headerRows,
    //   height,
    //   innerWidth,
    //   leftFixedWidth,
    //   onKeyDown,
    //   rightFixedWidth,
    //   rowHeight,
    //   scrollHeight,
    //   scrollWidth,
    //   totalColumns,
    //   totalRows,
    //   width,
    //   focusSignal,
    // });

    return (
      <GridBoundsContext.Provider value={boundSignal}>
        <FocusCellContext.Provider value={focusSignal}>
          <div
            aria-colcount={totalColumns}
            aria-rowcount={totalRows}
            className={cx(className, classes.gridWrapper)}
            ref={joinWrapperRef}
            role="grid"
            style={{
              width,
              height,
              direction,
            }}
          >
            {headerRows ? (
              <Header
                CellComponent={CellComponent}
                appUid={appUid}
                roleUid={roleUid}
                columnLefts={columnLefts}
                columnWidths={columnWidths}
                columns={columns}
                h1GroupColumnLefts={h1GroupColumnLefts ?? []}
                h1GroupColumnWidths={h1GroupColumnWidths ?? []}
                h2GroupColumnLefts={h2GroupColumnLefts ?? []}
                h2GroupColumnWidths={h2GroupColumnWidths ?? []}
                h3GroupColumnLefts={h3GroupColumnLefts ?? []}
                h3GroupColumnWidths={h3GroupColumnWidths ?? []}
                h1GroupColumns={h1GroupColumns ?? []}
                h2GroupColumns={h2GroupColumns ?? []}
                h3GroupColumns={h3GroupColumns ?? []}
                custom={custom}
                dataContext={dataContext}
                direction={direction}
                fixedLeftColumns={fixedLeftColumns}
                fixedRightColumns={fixedRightColumns}
                h1FixedLeftColumns={h1FixedLeftColumns}
                h1FixedRightColumns={h1FixedRightColumns}
                h2FixedLeftColumns={h2FixedLeftColumns}
                h2FixedRightColumns={h2FixedRightColumns}
                h3FixedLeftColumns={h3FixedLeftColumns}
                h3FixedRightColumns={h3FixedRightColumns}
                headerRows={headerRows}
                innerWidth={innerWidth}
                onClick={onClick}
                onKeyDown={onKeyDown}
                pageId={pageId}
                resetScroll={resetScroll}
                rowHeight={rowHeight}
                showColumnLines={showColumnLines}
                showRowLines={showRowLines}
                totalColumns={totalColumns}
                vertialScrollbarSize={vertialScrollbarSize}
                totalRows={totalRows}
                bodyHeight={bodyHeight}
                bodyWidth={bodyWidth}
              />
            ) : null}
            {headerRows && dataContext.showFilters ? (
              <GridFilter
                CellComponent={CellComponent}
                appUid={appUid}
                roleUid={roleUid}
                columnLefts={columnLefts}
                columnWidths={columnWidths}
                columns={columns}
                custom={custom}
                dataContext={dataContext}
                direction={direction}
                fixedLeftColumns={fixedLeftColumns}
                fixedRightColumns={fixedRightColumns}
                headerRows={headerRows}
                innerWidth={innerWidth}
                onClick={onClick}
                onKeyDown={onKeyDown}
                pageId={pageId}
                resetScroll={resetScroll}
                rowHeight={rowHeight}
                showColumnLines={showColumnLines}
                showRowLines={showRowLines}
                totalColumns={totalColumns}
                vertialScrollbarSize={vertialScrollbarSize}
                totalRows={totalRows}
              />
            ) : null}
            <Body
              CellComponent={CellComponent}
              appUid={appUid}
              roleUid={roleUid}
              bodyHeight={bodyHeight}
              bodyProps={bodyProps}
              bodyRef={bodyRef}
              columnLefts={columnLefts}
              columnWidths={columnWidths}
              columns={columns}
              custom={custom}
              dataContext={dataContext}
              direction={direction}
              fixedLeftColumns={fixedLeftColumns}
              fixedRightColumns={fixedRightColumns}
              footerRows={footerRows}
              headerRows={headerRows}
              height={height}
              highlightCell={highlightCell}
              highlightRow={highlightRow}
              horizontalScrollbarSize={horizontalScrollbarSize}
              innerWidth={innerWidth}
              onClick={onClick}
              onKeyDown={onKeyDown}
              onRowDoubleClick={onRowDoubleClick}
              pageId={pageId}
              ref={rootRef}
              noDataMsg={noDataMsg}
              showNoDataMsg={showNoDataMsg}
              resetScroll={resetScroll}
              rowHeight={rowHeight}
              scrollToRow={scrollToRow}
              scrollToColumn={scrollToColumn}
              showColumnLines={showColumnLines}
              showRowLines={showRowLines}
              totalColumns={totalColumns}
              totalRows={totalRows}
              vertialScrollbarSize={vertialScrollbarSize}
              width={width}
            />
            {footerRows ? (
              <Footer
                CellComponent={CellComponent}
                appUid={appUid}
                roleUid={roleUid}
                columnLefts={columnLefts}
                columnWidths={columnWidths}
                columns={columns}
                custom={custom}
                dataContext={dataContext}
                direction={direction}
                fixedLeftColumns={fixedLeftColumns}
                fixedRightColumns={fixedRightColumns}
                footerRows={footerRows}
                innerWidth={innerWidth}
                onClick={onClick}
                onKeyDown={onKeyDown}
                pageId={pageId}
                resetScroll={resetScroll}
                rowHeight={rowHeight}
                showColumnLines={showColumnLines}
                showRowLines={showRowLines}
                totalColumns={totalColumns}
                totalRows={totalRows}
                vertialScrollbarSize={vertialScrollbarSize}
                width={width}
              />
            ) : null}
            {totalColumns > 1 && !disableReorder ? (
              <ColumnReorderArrow
                appUid={appUid}
                columnLefts={columnLefts}
                columnWidths={columnWidths}
                columns={columns}
                fixedLeftColumns={fixedLeftColumns}
                fixedRightColumns={fixedRightColumns}
                gridRef={wrapperRef}
                height={height - headerRows * rowHeight}
                itemId={itemId}
                pageId={pageId}
                top={headerRows * rowHeight}
              />
            ) : null}
            {pending ? <GridLoadingIndicator pending={pending} /> : null}
            <ColumnMenuWrapper
              appUid={appUid}
              gridItemId={itemId}
              pageId={pageId}
              resetScroll={resetScroll}
              totalColumns={totalColumns}
            />
          </div>
        </FocusCellContext.Provider>
      </GridBoundsContext.Provider>
    );
  },
);

GridInternalFn.displayName = 'GridInternal';

const GridInternal = memo(GridInternalFn);

const Grid = React.forwardRef(
  (
    {
      CellComponent,
      appUid,
      roleUid,
      backgroundColor,
      bodyProps,
      bodyRef,
      className,
      columnWidth,
      columns,
      custom,
      dataContext,
      fixedLeftColumns = 0,
      fixedRightColumns = 0,
      footerRows = 0,
      headerRows = 1,
      height,
      highlightCell = true,
      highlightRow = true,
      itemId,
      onClick,
      pageId,
      rowHeight,
      scrollToColumn = 0,
      scrollToRow = 0,
      showBorder = true,
      showColumnLines = true,
      showRowLines = true,
      noDataMsg,
      showNoDataMsg,
      totalColumns,
      totalRows,
      width,
      onRowDoubleClick,
      disableReorder,
      expandLastColumn,
      autoFitColumns,
      listStore,
      queryOnNext,
      rowBuffer,
      columnBuffer,
      allowDownArrowRecordCreation,
      h1GroupColumns,
      h2GroupColumns,
      h3GroupColumns,
      h1LeftFixedColumnGroups,
      h1RightFixedColumnGroups,
      h1BodyColumnGroups,
      h2LeftFixedColumnGroups,
      h2RightFixedColumnGroups,
      h2BodyColumnGroups,
      h3LeftFixedColumnGroups,
      h3RightFixedColumnGroups,
      h3BodyColumnGroups,
      h1FixedLeftColumns = 0,
      h1FixedRightColumns = 0,
      h2FixedLeftColumns = 0,
      h2FixedRightColumns = 0,
      h3FixedLeftColumns = 0,
      h3FixedRightColumns = 0,
    }: GridProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const classes = useGridStyles();
    const _fixedLeftColumns = Math.min(fixedLeftColumns, totalColumns);
    const _fixedRightColumns = Math.min(
      fixedRightColumns,
      totalColumns - _fixedLeftColumns,
    );
    const _h1FixedLeftColumns = Math.min(
      h1FixedLeftColumns,
      h1GroupColumns?.length ?? 0,
    );
    const _h1FixedRightColumns = Math.min(
      h1FixedRightColumns,
      h1GroupColumns?.length ?? 0 - _h1FixedLeftColumns,
    );
    const _h2FixedLeftColumns = Math.min(
      h2FixedLeftColumns,
      h2GroupColumns?.length ?? 0,
    );
    const _h2FixedRightColumns = Math.min(
      h2FixedRightColumns,
      h2GroupColumns?.length ?? 0 - _h2FixedLeftColumns,
    );
    const _h3FixedLeftColumns = Math.min(
      h3FixedLeftColumns,
      h3GroupColumns?.length ?? 0,
    );
    const _h3FixedRightColumns = Math.min(
      h3FixedRightColumns,
      h3GroupColumns?.length ?? 0 - _h3FixedLeftColumns,
    );
    const _headerRows = Math.min(headerRows, totalRows);
    const _footerRows = Math.min(footerRows, totalRows - _headerRows);

    const cn = cx(className, {
      [classes.gridBorder]: showBorder,
    });

    return (
      <ScrollValueProvider
        height={height - (showBorder ? 2 : 0)}
        width={width - (showBorder ? 2 : 0)}
        totalRows={totalRows}
        itemId={itemId}
      >
        <GridInternal
          CellComponent={CellComponent}
          appUid={appUid}
          roleUid={roleUid}
          backgroundColor={backgroundColor}
          bodyProps={bodyProps}
          bodyRef={bodyRef}
          className={cn}
          columnWidth={columnWidth}
          columns={columns}
          custom={custom}
          dataContext={dataContext}
          fixedLeftColumns={_fixedLeftColumns}
          fixedRightColumns={_fixedRightColumns}
          footerRows={_footerRows}
          headerRows={_headerRows}
          height={height - (showBorder ? 2 : 0)}
          highlightCell={highlightCell}
          highlightRow={highlightRow}
          itemId={itemId}
          onClick={onClick}
          pageId={pageId}
          ref={ref}
          rowHeight={rowHeight}
          scrollToColumn={scrollToColumn}
          scrollToRow={scrollToRow}
          showBorder={showBorder}
          showColumnLines={showColumnLines}
          showRowLines={showRowLines}
          totalColumns={totalColumns}
          totalRows={totalRows}
          width={width - (showBorder ? 2 : 0)}
          onRowDoubleClick={onRowDoubleClick}
          expandLastColumn={expandLastColumn}
          disableReorder={disableReorder}
          autoFitColumns={autoFitColumns}
          listStore={listStore}
          queryOnNext={queryOnNext}
          rowBuffer={rowBuffer}
          columnBuffer={columnBuffer}
          noDataMsg={noDataMsg}
          showNoDataMsg={showNoDataMsg}
          allowDownArrowRecordCreation={allowDownArrowRecordCreation}
          h1GroupColumns={h1GroupColumns}
          h2GroupColumns={h2GroupColumns}
          h3GroupColumns={h3GroupColumns}
          h1LeftFixedColumnGroups={h1LeftFixedColumnGroups}
          h1RightFixedColumnGroups={h1RightFixedColumnGroups}
          h1BodyColumnGroups={h1BodyColumnGroups}
          h2LeftFixedColumnGroups={h2LeftFixedColumnGroups}
          h2RightFixedColumnGroups={h2RightFixedColumnGroups}
          h2BodyColumnGroups={h2BodyColumnGroups}
          h3LeftFixedColumnGroups={h3LeftFixedColumnGroups}
          h3RightFixedColumnGroups={h3RightFixedColumnGroups}
          h3BodyColumnGroups={h3BodyColumnGroups}
          h1FixedLeftColumns={_h1FixedLeftColumns}
          h1FixedRightColumns={_h1FixedRightColumns}
          h2FixedLeftColumns={_h2FixedLeftColumns}
          h2FixedRightColumns={_h2FixedRightColumns}
          h3FixedLeftColumns={_h3FixedLeftColumns}
          h3FixedRightColumns={_h3FixedRightColumns}
        />
      </ScrollValueProvider>
    );
  },
);

Grid.displayName = 'Grid';

export default Grid;
